import { EditUnifiedFieldDrawer } from '@modules/ManageUnifiedFields/components/EditUnifiedFieldDrawer';
import { AutoCompleteTextField } from '@modules/common/components/AutoCompleteTextField';
import { EqlFieldCommonMetadataSourceIcons } from '@modules/common/components/EqlFieldCommonMetadataInfo';
import { MultiColumnAutoComplete } from '@modules/common/components/MultiColumnAutoComplete';
import { Suggestion } from '@modules/common/components/MultiColumnAutoComplete/MultiColumnAutoCompleteExample/Suggestion';
import { SuggestionOption } from '@modules/common/components/MultiColumnAutoComplete/MultiColumnAutoCompleteExample/types';
import { useFetchQueryBuilderGroupOptions } from '@modules/common/hooks/useFetchQueryBuilderGroupOptions';
import { isAIFilterOption } from '@modules/common/utils';
import { trackCustomFilterEvents } from '@modules/queryBuilder/helpers';
import { useCanEditCustomFilter } from '@modules/queryBuilder/hooks';
import { QueryBuilderStoreProvider } from '@modules/queryBuilder/store/QueryBuilderStoreProvider';
import { EditRounded, InfoOutlined } from '@mui/icons-material';
import {
  AutocompleteInputChangeReason,
  AutocompleteProps,
  Box,
  IconButton,
  TextFieldProps,
  Tooltip,
  Typography,
  useControlled,
} from '@mui/material';
import { EqlField, EqlFieldValue, EqlFieldValueType } from 'generated/graphql';
import { debounce } from 'lodash';
import { ReactNode, SyntheticEvent, useEffect, useMemo, useState } from 'react';
import { useMount } from 'react-use';
import { EQLGroupFieldDescriptionTooltipContent } from '../EQLGroupFieldDescriptionTooltipContent';
import { EditCustomFilterModal } from '../EditCustomFilterModal';
import { EqlFieldCommonMetadataEditButton } from '../EqlFieldCommonMetadataInfo/EqlFieldCommonMetadataEditIcon';
import { useMuiModalContext } from '../Modal/ModalContext';
import { shouldRenderCustomGroup } from '../MultiColumnAutoComplete/utils';

interface EqlFilterGroupsAutoCompleteProps
  extends Omit<
    AutocompleteProps<SuggestionOption, false, false, false>,
    'onChange' | 'renderInput' | 'options'
  > {
  onChange: (params: {
    eqlField: EqlField;
    eqlFieldValue?: EqlFieldValue;
  }) => void;
  openOnFocus?: boolean;
  onViewMore?: (option: SuggestionOption) => void;
  onOpenAIFilterAutoComplete: () => void;
  renderInput?: (
    textFieldProps: TextFieldProps,
    activeOption?: SuggestionOption,
  ) => ReactNode;
  omitTextSearchOnEmptyTerm?: boolean;
  onLoadingChange?: (isLoading: boolean) => void;
}

export function EqlFilterGroupsAutoComplete({
  onClose,
  onChange,
  openOnFocus,
  onViewMore,
  renderInput,
  omitTextSearchOnEmptyTerm = false,
  inputValue: inputValueProp,
  onInputChange: onInputChangeProp,
  onLoadingChange,
  onOpenAIFilterAutoComplete,
  ...autoCompleteProps
}: EqlFilterGroupsAutoCompleteProps) {
  const [inputValue, setInputValue] = useControlled({
    controlled: inputValueProp,
    default: '',
    name: 'EqlFilterGroupsAutoComplete',
    state: 'inputValue',
  });
  const columnsLength = inputValue ? 1 : 2;
  const [fetchQueryBuilderGroupsAPI, { options, rows, isLoading }] =
    useFetchQueryBuilderGroupOptions({
      searchTerm: inputValue.trim(),
      fetchPolicy: 'cache-and-network',
    });
  const [shouldOpenUnifiedFieldDrawer, setShouldOpenUnifiedFieldDrawer] =
    useState(false);
  const [selectedUnifiedFieldForEdit, setSelectedUnifiedFieldForEdit] =
    useState<EqlField | undefined>();

  const canEditCustomFilter = useCanEditCustomFilter();

  useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [isLoading, onLoadingChange]);

  useMount(() => {
    fetchQueryBuilderGroupsAPI(undefined);
  });

  const fetchQueryBuilderGroups = useMemo(
    () =>
      debounce((searchTerm: string) => {
        fetchQueryBuilderGroupsAPI(searchTerm || undefined);
      }, 200),
    [fetchQueryBuilderGroupsAPI],
  );

  useEffect(() => {
    fetchQueryBuilderGroups(inputValue);
  }, [inputValue, fetchQueryBuilderGroups]);

  const onInputChange = (
    eve: SyntheticEvent<Element, Event>,
    value: string,
    reason: AutocompleteInputChangeReason,
  ) => {
    if (reason !== 'reset') {
      setInputValue(value);
      onInputChangeProp?.(eve, value, reason);
    }
  };

  const defaultRenderInput = (params: TextFieldProps) => (
    <AutoCompleteTextField
      {...params}
      placeholder="Filter your feedback like a barista filters their coffee"
    />
  );

  const { openModal, closeModal } = useMuiModalContext();

  const openEditCustomFilterModal = (option: EqlField) => {
    openModal({
      contentComponent: () => (
        <QueryBuilderStoreProvider>
          <EditCustomFilterModal
            onClose={closeModal}
            filterName={option.displayName}
            filterID={option.detail?.reference?.referenceID}
          />
        </QueryBuilderStoreProvider>
      ),
      modalProps: {
        fullWidth: true,
        maxWidth: 'md',
      },
    });
    trackCustomFilterEvents({
      eventName: 'Custom Filter Edit Button Clicked',
      filterName: option.displayName ?? '',
    });
  };
  const renderSecondaryAction = (option: SuggestionOption) => {
    if (isAIFilterOption(option)) {
      return null;
    }

    if (option.field.valueType === EqlFieldValueType.None) {
      if (!canEditCustomFilter(option.field.detail?.reference?.creatorID)) {
        return null;
      }
      return (
        <Box
          className="EditIconContainer"
          sx={{ opacity: 0 }}
          onClick={(event) => event.stopPropagation()}
        >
          <Tooltip title="Edit Filter" disableInteractive>
            <IconButton onClick={() => openEditCustomFilterModal(option.field)}>
              <EditRounded fontSize="small" color="action" />
            </IconButton>
          </Tooltip>
        </Box>
      );
    }

    return (
      <>
        <EqlFieldCommonMetadataSourceIcons
          eqlField={option.field}
          tooltipPlacement="bottom"
        />
        <EqlFieldCommonMetadataEditButton
          eqlField={option.field}
          setShouldOpenUnifiedFieldDrawer={setShouldOpenUnifiedFieldDrawer}
          setSelectedUnifiedFieldForEdit={setSelectedUnifiedFieldForEdit}
        />
      </>
    );
  };

  const renderGroup = (option: SuggestionOption) => {
    const groupDisplayName = option.groupDisplayName;
    const groupID = option.groupID;

    if (shouldRenderCustomGroup(groupID)) {
      return (
        <Typography pl={2} py={0.75} variant="subtitle2" color="text.secondary">
          {groupDisplayName}
          <Tooltip
            title={<EQLGroupFieldDescriptionTooltipContent groupID={groupID} />}
            arrow
          >
            <InfoOutlined fontSize="inherit" sx={{ ml: 1 }} />
          </Tooltip>
        </Typography>
      );
    }
    return null;
  };

  return (
    <>
      <MultiColumnAutoComplete
        {...autoCompleteProps}
        openOnFocus={openOnFocus}
        fullWidth
        columnsLength={columnsLength}
        inputValue={inputValue}
        onInputChange={onInputChange}
        options={options}
        rows={rows}
        loading={isLoading}
        noOptionsText="No suggestions found"
        renderInput={renderInput ?? defaultRenderInput}
        groupKey="groupID"
        renderGroup={(option) => renderGroup(option)}
        groupBy={(option) => option.groupDisplayName}
        renderOption={(optionProps, option) => {
          return (
            <Suggestion
              props={optionProps}
              option={option}
              renderSecondaryAction={() => renderSecondaryAction(option)}
            />
          );
        }}
        onClose={(eve, reason) => {
          if (reason !== 'toggleInput') {
            onClose?.(eve, reason);
          }
        }}
        onChange={(event, option) => {
          if (event.type === 'keydown' && isLoading) {
            return;
          }

          if (!option) {
            return;
          }

          if (isAIFilterOption(option) && !option.value) {
            onOpenAIFilterAutoComplete();
            return;
          }

          if (option.id.endsWith('_more')) {
            onViewMore?.(option);
            return;
          }

          onChange({
            eqlField: option.field,
            eqlFieldValue: option.value,
          });
        }}
        PaperProps={{
          sx: { maxHeight: '50vh' },
        }}
      />
      {selectedUnifiedFieldForEdit?.field && (
        <EditUnifiedFieldDrawer
          open={shouldOpenUnifiedFieldDrawer}
          metadataKey={selectedUnifiedFieldForEdit.field}
          onClose={() => {
            setShouldOpenUnifiedFieldDrawer(false);
            setSelectedUnifiedFieldForEdit(undefined);
          }}
        />
      )}
    </>
  );
}
